import { supabase } from './supabaseClient';
import { getLocalDateOnlyFromValue, getTodayLocalDateOnly } from './date';
import { logError, logWarn } from './logger';
import { TaskCategory, UserStats } from '../types';

export const EMPTY_USER_STATS: UserStats = {
  str: 0,
  int: 0,
  foc: 0,
  spi: 0,
  cha: 0
};

export const ATTRIBUTE_DETAILS = [
  {
    key: 'str',
    shortLabel: 'STR',
    name: 'Corpo',
    color: '#10b981',
    description: 'Forca fisica, disciplina corporal e consistencia no que exige acao.'
  },
  {
    key: 'int',
    shortLabel: 'INT',
    name: 'Mente',
    color: '#3b82f6',
    description: 'Clareza mental, estudo, raciocinio e capacidade de absorver conhecimento.'
  },
  {
    key: 'foc',
    shortLabel: 'FOC',
    name: 'Foco',
    color: '#a855f7',
    description: 'Execucao, profundidade, concentracao e entrega no trabalho.'
  },
  {
    key: 'spi',
    shortLabel: 'SPI',
    name: 'Espirito',
    color: '#f59e0b',
    description: 'Presenca, fe, rotina espiritual e alinhamento interno.'
  },
  {
    key: 'cha',
    shortLabel: 'CHA',
    name: 'Social',
    color: '#ec4899',
    description: 'Comunicacao, carisma, conexoes e energia nas relacoes.'
  }
] as const;

type StatKey = keyof UserStats;

type TaskCompletionStatRow = {
  completed_at: string;
  metadata?: Record<string, unknown> | null;
  task_id?: string | null;
};

type TaskCategoryRow = {
  id: string;
  category?: TaskCategory | null;
};

type ChallengeParticipantRow = {
  id: string;
};

type ChallengeCompletionRow = {
  challenge_task_id: string;
  completed_at: string;
  completion_date?: string | null;
  evidence_status?: 'approved' | 'rejected' | null;
};

type ChallengeTaskRow = {
  id: string;
  category?: TaskCategory | null;
  title: string;
  description?: string | null;
  challenge_id: string;
};

type ChallengeRow = {
  id: string;
  title: string;
  description?: string | null;
};

type FocusSessionStatRow = {
  duration: number;
  completed_at: string;
  type: 'focus' | 'break';
};

const STATS_CACHE_TTL_MS = 60 * 1000;
const ATTRIBUTE_DECAY_GRACE_DAYS = 3;
const ATTRIBUTE_DECAY_PER_DAY = 1;
const FOCUS_SECONDS_PER_POINT = 25 * 60;

const statsCache = new Map<string, { stats: UserStats; cachedAt: number }>();

const cloneStats = (stats?: Partial<UserStats> | null): UserStats => ({
  str: Math.max(0, Number(stats?.str || 0)),
  int: Math.max(0, Number(stats?.int || 0)),
  foc: Math.max(0, Number(stats?.foc || 0)),
  spi: Math.max(0, Number(stats?.spi || 0)),
  cha: Math.max(0, Number(stats?.cha || 0))
});

const areStatsEqual = (left: UserStats, right?: Partial<UserStats> | null) => (
  left.str === Math.max(0, Number(right?.str || 0))
  && left.int === Math.max(0, Number(right?.int || 0))
  && left.foc === Math.max(0, Number(right?.foc || 0))
  && left.spi === Math.max(0, Number(right?.spi || 0))
  && left.cha === Math.max(0, Number(right?.cha || 0))
);

const getStatKeyForCategory = (category?: TaskCategory | null): StatKey | null => {
  switch (category) {
    case 'Body':
      return 'str';
    case 'Mind':
      return 'int';
    case 'Work':
      return 'foc';
    case 'Spirit':
      return 'spi';
    case 'Life':
      return 'cha';
    default:
      return null;
  }
};

export const buildStatsDeltaFromCategory = (category?: TaskCategory | null): UserStats => {
  const key = getStatKeyForCategory(category);
  if (!key) {
    return { ...EMPTY_USER_STATS };
  }

  return {
    ...EMPTY_USER_STATS,
    [key]: 1
  };
};

export const inferChallengeTaskCategory = (
  challengeTitle: string,
  challengeDescription: string | null | undefined,
  taskTitle: string,
  taskDescription?: string | null
): TaskCategory => {
  const normalizedText = `${challengeTitle} ${challengeDescription || ''} ${taskTitle} ${taskDescription || ''}`
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (/(espirit|devocional|oracao|deus|gratidao|fe|biblia)/.test(normalizedText)) {
    return 'Spirit';
  }

  if (/(corpo|treino|atividade fisica|hidrat|agua|alongamento|sono|saude)/.test(normalizedText)) {
    return 'Body';
  }

  if (/(trabalho|foco|produtiv|pendenc|prioridad|deep work|sprint)/.test(normalizedText)) {
    return 'Work';
  }

  if (/(estudo|mente|mind|leitura|aprendizado|cogn)/.test(normalizedText)) {
    return 'Mind';
  }

  if (/(casa|familia|relacion|rotina|organizacao pessoal|life|vida)/.test(normalizedText)) {
    return 'Life';
  }

  return 'Work';
};

const addPointsToStat = (
  stats: UserStats,
  lastActivityByStat: Record<StatKey, string | null>,
  statKey: StatKey | null,
  points: number,
  completedAt: string | null
) => {
  if (!statKey || points <= 0) {
    return;
  }

  stats[statKey] += points;

  if (!completedAt) {
    return;
  }

  const currentDate = lastActivityByStat[statKey];
  if (!currentDate || completedAt > currentDate) {
    lastActivityByStat[statKey] = completedAt;
  }
};

const getFocusPoints = (durationSeconds: number) => {
  if (durationSeconds < FOCUS_SECONDS_PER_POINT) {
    return 0;
  }

  return Math.max(1, Math.floor(durationSeconds / FOCUS_SECONDS_PER_POINT));
};

const getInactiveDayCount = (lastActivityDate: string, todayDate: string) => {
  const lastActivity = new Date(`${lastActivityDate}T12:00:00`);
  const today = new Date(`${todayDate}T12:00:00`);
  const diffMs = today.getTime() - lastActivity.getTime();

  if (diffMs <= 0) {
    return 0;
  }

  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

const applyAttributeDecay = (
  baseStats: UserStats,
  lastActivityByStat: Record<StatKey, string | null>
): UserStats => {
  const todayDate = getTodayLocalDateOnly();
  const nextStats = cloneStats(baseStats);

  (Object.keys(nextStats) as StatKey[]).forEach((statKey) => {
    const lastActivityDate = lastActivityByStat[statKey];
    if (!lastActivityDate || nextStats[statKey] <= 0) {
      nextStats[statKey] = 0;
      return;
    }

    const inactiveDays = getInactiveDayCount(lastActivityDate, todayDate);
    const decayDays = Math.max(0, inactiveDays - ATTRIBUTE_DECAY_GRACE_DAYS);
    const decay = decayDays * ATTRIBUTE_DECAY_PER_DAY;
    nextStats[statKey] = Math.max(0, nextStats[statKey] - decay);
  });

  return nextStats;
};

const loadTaskCompletionStats = async (userId: string) => {
  const { data: completionRows, error: completionsError } = await supabase
    .from('task_completions')
    .select('task_id, completed_at, metadata')
    .eq('user_id', userId);

  if (completionsError) {
    logError('stats', 'Falha ao buscar task_completions para montar atributos.', {
      userId,
      error: completionsError
    });
    return [];
  }

  return (completionRows as TaskCompletionStatRow[]) || [];
};

const loadTaskCategories = async (taskIds: string[]) => {
  if (!taskIds.length) {
    return new Map<string, TaskCategory | null>();
  }

  const { data: taskRows, error: tasksError } = await supabase
    .from('tasks')
    .select('id, category')
    .in('id', taskIds);

  if (tasksError) {
    logWarn('stats', 'Falha ao buscar categorias das tasks para atributos. Vou usar o metadata salvo.', tasksError);
    return new Map<string, TaskCategory | null>();
  }

  return new Map(
    (((taskRows as TaskCategoryRow[]) || []).map((task) => [task.id, task.category || null])) as Array<[string, TaskCategory | null]>
  );
};

const loadChallengeCompletionStats = async (userId: string) => {
  const { data: participantRows, error: participantsError } = await supabase
    .from('challenge_participants')
    .select('id')
    .eq('user_id', userId);

  if (participantsError) {
    logError('stats', 'Falha ao buscar participacoes de challenge para montar atributos.', {
      userId,
      error: participantsError
    });
    return {
      completions: [] as ChallengeCompletionRow[],
      challengeTasksById: new Map<string, ChallengeTaskRow>(),
      challengesById: new Map<string, ChallengeRow>()
    };
  }

  const participantIds = ((participantRows as ChallengeParticipantRow[]) || []).map((participant) => participant.id).filter(Boolean);
  if (!participantIds.length) {
    return {
      completions: [] as ChallengeCompletionRow[],
      challengeTasksById: new Map<string, ChallengeTaskRow>(),
      challengesById: new Map<string, ChallengeRow>()
    };
  }

  const { data: completionRows, error: completionError } = await supabase
    .from('challenge_task_completions')
    .select('challenge_task_id, completed_at, completion_date, evidence_status')
    .in('participant_id', participantIds);

  if (completionError) {
    logError('stats', 'Falha ao buscar conclusoes de challenge para montar atributos.', {
      userId,
      error: completionError
    });
    return {
      completions: [] as ChallengeCompletionRow[],
      challengeTasksById: new Map<string, ChallengeTaskRow>(),
      challengesById: new Map<string, ChallengeRow>()
    };
  }

  const validCompletions = (((completionRows as ChallengeCompletionRow[]) || []).filter(
    (completion) => completion.evidence_status !== 'rejected'
  ));
  const challengeTaskIds = [...new Set(validCompletions.map((completion) => completion.challenge_task_id).filter(Boolean))];

  if (!challengeTaskIds.length) {
    return {
      completions: validCompletions,
      challengeTasksById: new Map<string, ChallengeTaskRow>(),
      challengesById: new Map<string, ChallengeRow>()
    };
  }

  const { data: challengeTaskRows, error: challengeTasksError } = await supabase
    .from('challenge_tasks')
    .select('id, category, title, description, challenge_id')
    .in('id', challengeTaskIds);

  if (challengeTasksError) {
    logError('stats', 'Falha ao buscar dados das tasks de challenge para atributos.', {
      userId,
      error: challengeTasksError
    });
    return {
      completions: validCompletions,
      challengeTasksById: new Map<string, ChallengeTaskRow>(),
      challengesById: new Map<string, ChallengeRow>()
    };
  }

  const challengeTasks = (challengeTaskRows as ChallengeTaskRow[]) || [];
  const challengeIds = [...new Set(challengeTasks.map((task) => task.challenge_id).filter(Boolean))];
  const { data: challengeRows, error: challengesError } = challengeIds.length
    ? await supabase
        .from('challenges')
        .select('id, title, description')
        .in('id', challengeIds)
    : { data: [], error: null };

  if (challengesError) {
    logWarn('stats', 'Falha ao buscar contexto dos challenges para inferir categoria. Vou seguir so com o que ja tenho.', challengesError);
  }

  return {
    completions: validCompletions,
    challengeTasksById: new Map(challengeTasks.map((task) => [task.id, task])),
    challengesById: new Map((((challengeRows as ChallengeRow[]) || []).map((challenge) => [challenge.id, challenge])) as Array<[string, ChallengeRow]>)
  };
};

const loadFocusCompletionStats = async (userId: string) => {
  const { data: focusRows, error: focusError } = await supabase
    .from('focus_sessions')
    .select('duration, completed_at, type')
    .eq('user_id', userId)
    .eq('type', 'focus');

  if (focusError) {
    logError('stats', 'Falha ao buscar sessoes de foco para montar atributos.', {
      userId,
      error: focusError
    });
    return [];
  }

  return (focusRows as FocusSessionStatRow[]) || [];
};

const computeStatsFromActivity = (input: {
  taskCompletions: TaskCompletionStatRow[];
  taskCategoriesById: Map<string, TaskCategory | null>;
  challengeCompletions: ChallengeCompletionRow[];
  challengeTasksById: Map<string, ChallengeTaskRow>;
  challengesById: Map<string, ChallengeRow>;
  focusSessions: FocusSessionStatRow[];
}) => {
  const baseStats = { ...EMPTY_USER_STATS };
  const lastActivityByStat: Record<StatKey, string | null> = {
    str: null,
    int: null,
    foc: null,
    spi: null,
    cha: null
  };

  input.taskCompletions.forEach((completion) => {
    const metadataCategory = typeof completion.metadata?.category === 'string'
      ? completion.metadata.category as TaskCategory
      : null;
    const category = metadataCategory || (completion.task_id ? input.taskCategoriesById.get(completion.task_id) || null : null);
    const statKey = getStatKeyForCategory(category);
    addPointsToStat(baseStats, lastActivityByStat, statKey, 1, getLocalDateOnlyFromValue(completion.completed_at));
  });

  input.challengeCompletions.forEach((completion) => {
    const challengeTask = input.challengeTasksById.get(completion.challenge_task_id);
    if (!challengeTask) {
      return;
    }

    const challenge = input.challengesById.get(challengeTask.challenge_id);
    const category = challengeTask.category || inferChallengeTaskCategory(
      challenge?.title || '',
      challenge?.description || null,
      challengeTask.title,
      challengeTask.description
    );

    addPointsToStat(
      baseStats,
      lastActivityByStat,
      getStatKeyForCategory(category),
      1,
      completion.completion_date || getLocalDateOnlyFromValue(completion.completed_at)
    );
  });

  input.focusSessions.forEach((session) => {
    addPointsToStat(
      baseStats,
      lastActivityByStat,
      'foc',
      getFocusPoints(Number(session.duration || 0)),
      getLocalDateOnlyFromValue(session.completed_at)
    );
  });

  return applyAttributeDecay(baseStats, lastActivityByStat);
};

export const invalidateProfileStatsCache = (userId?: string | null) => {
  if (!userId) {
    return;
  }

  statsCache.delete(userId);
};

export const syncProfileStatsFromActivity = async (
  userId: string,
  options?: {
    currentStats?: UserStats | null;
    force?: boolean;
  }
): Promise<UserStats> => {
  const cached = statsCache.get(userId);
  const now = Date.now();

  if (!options?.force && cached && (now - cached.cachedAt) < STATS_CACHE_TTL_MS) {
    return cached.stats;
  }

  const [taskCompletions, challengeData, focusSessions] = await Promise.all([
    loadTaskCompletionStats(userId),
    loadChallengeCompletionStats(userId),
    loadFocusCompletionStats(userId)
  ]);

  const taskIds = [...new Set(taskCompletions.map((completion) => completion.task_id).filter((taskId): taskId is string => Boolean(taskId)))];
  const taskCategoriesById = await loadTaskCategories(taskIds);
  const computedStats = computeStatsFromActivity({
    taskCompletions,
    taskCategoriesById,
    challengeCompletions: challengeData.completions,
    challengeTasksById: challengeData.challengeTasksById,
    challengesById: challengeData.challengesById,
    focusSessions
  });

  if (!areStatsEqual(computedStats, options?.currentStats)) {
    const { error } = await supabase
      .from('profiles')
      .update({ stats: computedStats })
      .eq('id', userId);

    if (error) {
      logError('stats', 'Falha ao sincronizar atributos calculados no perfil.', {
        userId,
        error
      });
    }
  }

  statsCache.set(userId, {
    stats: computedStats,
    cachedAt: now
  });

  return computedStats;
};
